/**
 * QR photos decoded in the browser, Q6 and D36.
 *
 * `BarcodeDetector` is the platform's own decoder, so no package is added
 * (rule 5). TypeScript's DOM library does not carry it yet, hence the local
 * declaration. It exists in Chrome and Edge on macOS, ChromeOS and Android,
 * not on Windows or Linux desktops, and not in Firefox or Safari: whether it
 * can read QR is asked of the browser, never assumed from its name.
 */

export interface DetectedCode {
  rawValue: string;
}

export interface QrDetector {
  detect(image: ImageBitmapSource): Promise<DetectedCode[]>;
}

interface DetectorClass {
  new (options: { formats: string[] }): QrDetector;
  getSupportedFormats(): Promise<string[]>;
}

const detectorClass = (): DetectorClass | undefined =>
  (globalThis as { BarcodeDetector?: DetectorClass }).BarcodeDetector;

/** A QR detector, or null when this browser cannot read QR from an image. */
export async function qrDetector(): Promise<QrDetector | null> {
  const Detector = detectorClass();
  if (Detector === undefined) return null;
  try {
    const formats = await Detector.getSupportedFormats();
    return formats.includes('qr_code') ? new Detector({ formats: ['qr_code'] }) : null;
  } catch {
    return null;
  }
}

export interface PhotoReading {
  /** Every QR read, in file order, a photo of several codes giving several. */
  payloads: string[];
  /** The files in which no QR could be read. */
  unread: string[];
}

export async function readPhotos(
  files: readonly File[],
  detector: QrDetector,
  decode: (file: File) => Promise<ImageBitmapSource> = (file) => createImageBitmap(file),
): Promise<PhotoReading> {
  const reading: PhotoReading = { payloads: [], unread: [] };
  for (const file of files) {
    let found: string[];
    try {
      const codes = await detector.detect(await decode(file));
      found = codes.map((c) => c.rawValue.trim()).filter((v) => v !== '');
    } catch {
      found = [];
    }
    if (found.length === 0) reading.unread.push(file.name);
    else reading.payloads.push(...found);
  }
  return reading;
}

/** `3 photos read, 4 codes found. No QR in IMG_2.jpg.` */
export function readingText(files: number, reading: PhotoReading): string {
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const head = `${plural(files, 'photo')} read, ${plural(reading.payloads.length, 'code')} found.`;
  return reading.unread.length > 0 ? `${head} No QR in ${reading.unread.join(', ')}.` : head;
}
